import React, { useState, useEffect, useRef } from "react";
import { getBotDashboard } from "../../services/api";
import "./QueriesLog.css";

// Interface mapped exactly to the dashboard API response item
interface QueryRecord {
  id: string;
  query: string;
  user_audio: string | null;
  answer: string;
  answer_id: string;
  answer_audio: string | null;
  channel: string;
  input_type: string;
  language: string;
  sessionToken: string;
  userAgent: string;
  failed_to_answer: boolean;
  createdAt: string; // ISO date string e.g., "2026-07-29T05:48:09.773Z"
  updatedAt: string;
  indent_type: string;
  name: string;
  email: string | null;
  date_diff_days: number;
}

export const QueriesLog: React.FC = () => {
  // Date states in YYYY-MM-DD
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7); // Default to last 7 days
    return d.toISOString().split("T")[0];
  });
  const [toDate, setToDate] = useState(() => {
    return new Date().toISOString().split("T")[0];
  });

  // Calendar visibility & focus
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectingField, setSelectingField] = useState<"from" | "to">("from"); // Active underline segment
  const [currentMonthLeft, setCurrentMonthLeft] = useState(() => {
    // Default calendar view focus to July 2026 as per mockup, or current month
    return new Date(2026, 6, 1); // July 2026
  });
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const datepickerRef = useRef<HTMLDivElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [records, setRecords] = useState<QueryRecord[]>([]);
  const [allQueries, setAllQueries] = useState(true);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Column level search states
  const [querySearch, setQuerySearch] = useState("");
  const [showQuerySearch, setShowQuerySearch] = useState(false);
  const [tempQuerySearch, setTempQuerySearch] = useState("");
  const querySearchRef = useRef<HTMLDivElement>(null);

  const [answerSearch, setAnswerSearch] = useState("");
  const [showAnswerSearch, setShowAnswerSearch] = useState(false);
  const [tempAnswerSearch, setTempAnswerSearch] = useState("");
  const answerSearchRef = useRef<HTMLDivElement>(null);

  const handleToggleQuerySearch = () => {
    if (!showQuerySearch) {
      setTempQuerySearch(querySearch);
    }
    setShowQuerySearch(!showQuerySearch);
  };

  const handleToggleAnswerSearch = () => {
    if (!showAnswerSearch) {
      setTempAnswerSearch(answerSearch);
    }
    setShowAnswerSearch(!showAnswerSearch);
  };

  const handleQuerySearchSubmit = () => {
    setQuerySearch(tempQuerySearch);
    setShowQuerySearch(false);
  };

  const handleAnswerSearchSubmit = () => {
    setAnswerSearch(tempAnswerSearch);
    setShowAnswerSearch(false);
  };

  const handleQuerySearchReset = () => {
    setTempQuerySearch("");
    setQuerySearch("");
    setShowQuerySearch(false);
  };

  const handleAnswerSearchReset = () => {
    setTempAnswerSearch("");
    setAnswerSearch("");
    setShowAnswerSearch(false);
  };

  // Expandable Answer cell states
  const [expandedAnswers, setExpandedAnswers] = useState<Record<string, boolean>>({});

  // State to track if the table has been scrolled horizontally
  const [scrolled, setScrolled] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Audio Playback state & ref
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);

  // Helper to trigger toast notification
  const triggerToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };

  // Close calendar picker and search dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      if (datepickerRef.current && !datepickerRef.current.contains(targetNode)) {
        setIsPickerOpen(false);
      }
      if (querySearchRef.current && !querySearchRef.current.contains(targetNode)) {
        setShowQuerySearch(false);
      }
      if (answerSearchRef.current && !answerSearchRef.current.contains(targetNode)) {
        setShowAnswerSearch(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch data from API
  const fetchQueries = async (isRefresh = false) => {
    stopCurrentAudio();
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await getBotDashboard(fromDate, toDate);
      if (response && Array.isArray(response.data)) {
        setRecords(response.data);
      } else if (Array.isArray(response)) {
        setRecords(response);
      } else {
        setRecords([]);
      }
      
      if (isRefresh) {
        triggerToast("Queries log refreshed successfully!");
      }
    } catch (err: any) {
      console.error("Error fetching queries log:", err);
      setError("Failed to fetch query logs. Please check connection.");
      triggerToast("Error fetching query logs");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fetch when dates change
  useEffect(() => {
    fetchQueries();
  }, [fromDate, toDate]);

  // Reset to page 1 when filters or search inputs change
  useEffect(() => {
    setCurrentPage(1);
  }, [querySearch, answerSearch, fromDate, toDate, allQueries]);

  const handleRefresh = () => {
    fetchQueries(true);
  };

  const handleExport = () => {
    if (filteredRecords.length === 0) {
      triggerToast("No query logs to export.");
      return;
    }

    // Helper to escape values for CSV to prevent delimiter issues
    const escapeCSV = (val: any) => {
      if (val === null || val === undefined) return "";
      const stringVal = String(val).trim();
      // Double quotes must be escaped by doubling them
      return `"${stringVal.replace(/"/g, '""')}"`;
    };

    // CSV Headers matching active columns
    const headers = [
      "S. No.",
      "Query",
      "Answer",
      "Answer ID",
      "Answer Audio",
      "Input Type",
      "Language",
      "Session Token",
      "Failed to Answer",
      "Created At"
    ];

    // CSV Rows mapping
    const rows = filteredRecords.map((record, index) => [
      index + 1,
      record.query || "",
      record.answer || "",
      record.answer_id || "",
      record.answer_audio || "",
      record.input_type || "",
      record.language || "",
      record.sessionToken || "",
      record.failed_to_answer ? "Yes" : "No",
      formatDateTime(record.createdAt)
    ]);

    // Construct CSV content (including UTF-8 BOM for Excel compatibility)
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map(escapeCSV).join(","))
    ].join("\n");

    try {
      const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      const dateStr = new Date().toISOString().split("T")[0];
      link.setAttribute("download", `queries_log_${dateStr}.csv`);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      triggerToast("Queries log exported to CSV!");
    } catch (err) {
      console.error("CSV Export Error:", err);
      triggerToast("Failed to export queries log.");
    }
  };

  const toggleExpandAnswer = (id: string) => {
    setExpandedAnswers((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Helper to format Date into YYYY-MM-DD
  const formatDateToString = (date: Date): string => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // Helper to format ISO strings into readable "DD/MM/YYYY hh:mm AM/PM"
  const formatDateTime = (isoString: string): string => {
    if (!isoString) return "";
    try {
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return isoString;

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12; // hour '0' should be '12'

      return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
    } catch {
      return isoString;
    }
  };

  // Format YYYY-MM-DD to YYYY/MM/DD for header trigger box matching screenshot
  const formatDisplayDate = (dateStr: string): string => {
    if (!dateStr) return "";
    return dateStr.replace(/-/g, "/");
  };

  // Helper to stop currently playing audio
  const stopCurrentAudio = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
    setPlayingAudioId(null);
  };

  // Audio Playback helper with toggle (play/stop) & mutual exclusion
  const playAudio = (id: string, url: string) => {
    if (!url) return;

    // If clicking the currently playing audio, stop it
    if (playingAudioId === id) {
      stopCurrentAudio();
      return;
    }

    // Stop any other currently playing audio first
    stopCurrentAudio();

    try {
      const audio = new Audio(url);
      currentAudioRef.current = audio;
      setPlayingAudioId(id);

      audio.onended = () => {
        if (currentAudioRef.current === audio) {
          currentAudioRef.current = null;
          setPlayingAudioId(null);
        }
      };

      audio.onerror = () => {
        console.error("Audio playback error");
        triggerToast("Error playing audio.");
        if (currentAudioRef.current === audio) {
          currentAudioRef.current = null;
          setPlayingAudioId(null);
        }
      };

      audio.play().catch((err) => {
        console.error("Audio playback error:", err);
        triggerToast("Playback blocked by browser settings.");
        if (currentAudioRef.current === audio) {
          currentAudioRef.current = null;
          setPlayingAudioId(null);
        }
      });
    } catch (err) {
      console.error("Audio setup error:", err);
      triggerToast("Could not play audio.");
      currentAudioRef.current = null;
      setPlayingAudioId(null);
    }
  };

  // Stop audio playback when component unmounts
  useEffect(() => {
    return () => {
      stopCurrentAudio();
    };
  }, []);

  // Scroll handler to toggle sticky drop-shadow highlight dynamically
  const handleTableScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    setScrolled(el.scrollLeft > 0);

    // Show scrollbar on scroll — hide it again 600ms after scrolling stops
    el.classList.add("is-scrolling");
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      el.classList.remove("is-scrolling");
    }, 600);
  };

  // Filter records locally by column searches and allQueries toggle
  const filteredRecords = records.filter((record) => {
    // 1. Column Search Query Filter
    if (querySearch && !record.query?.toLowerCase().includes(querySearch.toLowerCase())) {
      return false;
    }

    // 2. Column Search Answer Filter
    if (answerSearch && !record.answer?.toLowerCase().includes(answerSearch.toLowerCase())) {
      return false;
    }

    // 3. All Queries Switch Filter (if false, filter out items where failed_to_answer is true)
    if (!allQueries && record.failed_to_answer) {
      return false;
    }

    return true;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredRecords.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedRecords = filteredRecords.slice(startIndex, startIndex + pageSize);

  // Dual-Calendar calculations
  const leftMonthName = currentMonthLeft.toLocaleString("default", { month: "short" });
  const leftYear = currentMonthLeft.getFullYear();

  const rightMonthDate = new Date(currentMonthLeft.getFullYear(), currentMonthLeft.getMonth() + 1, 1);
  const rightMonthName = rightMonthDate.toLocaleString("default", { month: "short" });
  const rightYear = rightMonthDate.getFullYear();

  const changeMonth = (offset: number) => {
    setCurrentMonthLeft(new Date(currentMonthLeft.getFullYear(), currentMonthLeft.getMonth() + offset, 1));
  };

  const changeYear = (offset: number) => {
    setCurrentMonthLeft(new Date(currentMonthLeft.getFullYear() + offset, currentMonthLeft.getMonth(), 1));
  };

  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay(); // 0 = Sun, 6 = Sat
  };

  // Generate 42 calendar grid cells (padding with preceding & succeeding months)
  const generateMonthDays = (baseDate: Date) => {
    const year = baseDate.getFullYear();
    const month = baseDate.getMonth();
    
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    
    const cells = [];
    
    // Prev month padding days
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const daysInPrevMonth = getDaysInMonth(prevYear, prevMonth);
    for (let i = firstDay - 1; i >= 0; i--) {
      cells.push({
        date: new Date(prevYear, prevMonth, daysInPrevMonth - i),
        isCurrentMonth: false
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      cells.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Next month padding days
    const remaining = 42 - cells.length;
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    for (let i = 1; i <= remaining; i++) {
      cells.push({
        date: new Date(nextYear, nextMonth, i),
        isCurrentMonth: false
      });
    }
    
    return cells;
  };

  // Cell highlighting classes
  const getDayClass = (dayDate: Date, isCurrentMonth: boolean) => {
    let classes = "calendar-day-cell";
    if (!isCurrentMonth) {
      classes += " out-of-month";
      return classes;
    }

    const dateStr = formatDateToString(dayDate);
    const fromTime = new Date(fromDate).getTime();
    const toTime = new Date(toDate).getTime();
    const cellTime = dayDate.getTime();

    if (dateStr === fromDate) {
      classes += " is-start-date";
    } else if (dateStr === toDate) {
      classes += " is-end-date";
    } else if (cellTime > fromTime && cellTime < toTime) {
      classes += " is-in-range";
    } else if (
      selectingField === "to" &&
      hoverDate &&
      cellTime > fromTime &&
      cellTime <= hoverDate.getTime()
    ) {
      classes += " is-hover-range";
    }

    return classes;
  };

  // Day click selection logic
  const handleDateClick = (date: Date) => {
    const dateStr = formatDateToString(date);
    if (selectingField === "from") {
      setFromDate(dateStr);
      setSelectingField("to"); // auto switch to end date
    } else {
      const from = new Date(fromDate);
      if (date < from) {
        setFromDate(dateStr);
        setToDate(fromDate);
      } else {
        setToDate(dateStr);
      }
      setSelectingField("from"); // reset active segment
      setIsPickerOpen(false); // auto-close on selection completion
    }
  };

  // Quick Preset Handlers
  const handlePresetSelect = (preset: string) => {
    const today = new Date();
    let start = new Date();
    let end = new Date();

    switch (preset) {
      case "today":
        start = today;
        end = today;
        break;
      case "yesterday":
        start = new Date();
        start.setDate(today.getDate() - 1);
        end = new Date();
        end.setDate(today.getDate() - 1);
        break;
      case "last7":
        start = new Date();
        start.setDate(today.getDate() - 7);
        end = today;
        break;
      case "last30":
        start = new Date();
        start.setDate(today.getDate() - 30);
        end = today;
        break;
      case "thisMonth":
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case "lastMonth":
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
    }

    setFromDate(formatDateToString(start));
    setToDate(formatDateToString(end));
    setSelectingField("from");
    setIsPickerOpen(false);
  };

  const leftDays = generateMonthDays(currentMonthLeft);
  const rightDays = generateMonthDays(rightMonthDate);
  const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  return (
    <div className="queries-log-container">
      {/* Date selector and Controls header card */}
      <div className="queries-header-card">
        <div className="queries-datepicker-container" ref={datepickerRef}>
          <span className="datepicker-label">
            <i className="fa-regular fa-calendar-days" /> Select Date Range:
          </span>
          <div className="queries-datepicker-wrapper" id="queries-datepicker-wrapper-id">
            {/* Input Trigger Box */}
            <div
              className={`queries-datepicker-trigger ${isPickerOpen ? "active" : ""}`}
              onClick={() => setIsPickerOpen(!isPickerOpen)}
            >
              <div
                className={`date-field-val ${selectingField === "from" ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectingField("from");
                  setIsPickerOpen(true);
                }}
              >
                {formatDisplayDate(fromDate)}
              </div>
              <span className="date-separator">➔</span>
              <div
                className={`date-field-val ${selectingField === "to" ? "active" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectingField("to");
                  setIsPickerOpen(true);
                }}
              >
                {formatDisplayDate(toDate)}
              </div>
              <i className="fa-regular fa-calendar date-calendar-icon" />
            </div>

            {/* Calendar Dropdown Dropdown */}
            {isPickerOpen && (
              <div className="calendar-dropdown-card">
                {/* Year/Month Selection Navigation */}
                <div className="calendar-header">
                  <div className="header-nav">
                    <button className="nav-btn" onClick={() => changeYear(-1)}>«</button>
                    <button className="nav-btn" onClick={() => changeMonth(-1)}>‹</button>
                  </div>
                  <div className="header-months-title">
                    <span className="month-title">{leftMonthName} {leftYear}</span>
                    <span className="month-title">{rightMonthName} {rightYear}</span>
                  </div>
                  <div className="header-nav">
                    <button className="nav-btn" onClick={() => changeMonth(1)}>›</button>
                    <button className="nav-btn" onClick={() => changeYear(1)}>»</button>
                  </div>
                </div>

                {/* Grids Display */}
                <div className="calendar-grids-container">
                  {/* Left Calendar Grid (Jul) */}
                  <div className="calendar-grid-month">
                    <div className="week-headers">
                      {weekDays.map((d) => (
                        <span key={d} className="week-header-day">{d}</span>
                      ))}
                    </div>
                    <div className="days-grid">
                      {leftDays.map((cell, i) => (
                        <div
                          key={`left-${i}`}
                          className={getDayClass(cell.date, cell.isCurrentMonth)}
                          onMouseEnter={() => setHoverDate(cell.date)}
                          onClick={() => handleDateClick(cell.date)}
                        >
                          {cell.date.getDate()}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right Calendar Grid (Aug) */}
                  <div className="calendar-grid-month">
                    <div className="week-headers">
                      {weekDays.map((d) => (
                        <span key={d} className="week-header-day">{d}</span>
                      ))}
                    </div>
                    <div className="days-grid">
                      {rightDays.map((cell, i) => (
                        <div
                          key={`right-${i}`}
                          className={getDayClass(cell.date, cell.isCurrentMonth)}
                          onMouseEnter={() => setHoverDate(cell.date)}
                          onClick={() => handleDateClick(cell.date)}
                        >
                          {cell.date.getDate()}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Presets List */}
                <div className="calendar-presets-container">
                  <button className="preset-btn" onClick={() => handlePresetSelect("today")}>Today</button>
                  <button className="preset-btn" onClick={() => handlePresetSelect("yesterday")}>Yesterday</button>
                  <button className="preset-btn" onClick={() => handlePresetSelect("last7")}>Last 7 Days</button>
                  <button className="preset-btn" onClick={() => handlePresetSelect("last30")}>Last 30 Days</button>
                  <button className="preset-btn" onClick={() => handlePresetSelect("thisMonth")}>This Month</button>
                  <button className="preset-btn" onClick={() => handlePresetSelect("lastMonth")}>Last Month</button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="queries-actions-container">
          <button className="queries-btn queries-refresh-btn" onClick={handleRefresh} disabled={loading || refreshing}>
            <i className={`fa-solid fa-rotate ${refreshing ? "fa-spin" : ""}`} />
            Refresh
          </button>

          <div className="all-queries-toggle">
            <span className="toggle-label">All Queries</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={allQueries}
                onChange={(e) => setAllQueries(e.target.checked)}
              />
              <span className="slider round"></span>
            </label>
          </div>

          <button className="queries-btn queries-export-btn" onClick={handleExport}>
            <i className="fa-solid fa-download" />
            Export
          </button>
        </div>
      </div>

      {/* Queries Table Card */}
      <div className="queries-table-card">
        <div className={`queries-table-wrapper ${scrolled ? "is-scrolled" : ""}`} onScroll={handleTableScroll}>
          <table className="queries-table">
            <thead>
              <tr>
                <th className="sno-col">S. No.</th>
                <th className="query-col">
                  <div className="header-content-wrapper" ref={querySearchRef}>
                    <span>Query</span>
                    <i
                      className={`fa-solid fa-magnifying-glass column-search-trigger ${
                        showQuerySearch || querySearch ? "active" : ""
                      }`}
                      onClick={handleToggleQuerySearch}
                    />
                    {showQuerySearch && (
                      <div className="column-search-dropdown-card">
                        <input
                          type="text"
                          className="column-search-dropdown-input"
                          placeholder="Search query"
                          value={tempQuerySearch}
                          onChange={(e) => setTempQuerySearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleQuerySearchSubmit();
                            } else if (e.key === "Escape") {
                              setShowQuerySearch(false);
                            }
                          }}
                          autoFocus
                        />
                        <div className="column-search-dropdown-actions">
                          <button
                            type="button"
                            className="column-search-dropdown-btn-search"
                            onClick={handleQuerySearchSubmit}
                          >
                            <i className="fa-solid fa-magnifying-glass btn-icon" />
                            Search
                          </button>
                          <button
                            type="button"
                            className="column-search-dropdown-btn-reset"
                            onClick={handleQuerySearchReset}
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </th>
                <th className="answer-col">
                  <div className="header-content-wrapper" ref={answerSearchRef}>
                    <span>Answer</span>
                    <i
                      className={`fa-solid fa-magnifying-glass column-search-trigger ${
                        showAnswerSearch || answerSearch ? "active" : ""
                      }`}
                      onClick={handleToggleAnswerSearch}
                    />
                    {showAnswerSearch && (
                      <div className="column-search-dropdown-card">
                        <input
                          type="text"
                          className="column-search-dropdown-input"
                          placeholder="Search response"
                          value={tempAnswerSearch}
                          onChange={(e) => setTempAnswerSearch(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleAnswerSearchSubmit();
                            } else if (e.key === "Escape") {
                              setShowAnswerSearch(false);
                            }
                          }}
                          autoFocus
                        />
                        <div className="column-search-dropdown-actions">
                          <button
                            type="button"
                            className="column-search-dropdown-btn-search"
                            onClick={handleAnswerSearchSubmit}
                          >
                            <i className="fa-solid fa-magnifying-glass btn-icon" />
                            Search
                          </button>
                          <button
                            type="button"
                            className="column-search-dropdown-btn-reset"
                            onClick={handleAnswerSearchReset}
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </th>
                <th className="uuid-col">Answer ID</th>
                <th className="audio-col">Answer Audio</th>
                <th className="short-col">Input Type</th>
                <th className="short-col">Language</th>
                <th className="uuid-col">Session Token</th>
                <th className="short-col">Failed to Answer</th>
                <th className="date-col">Date and Time (IST)</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRecords.length > 0 &&
                paginatedRecords.map((record, index) => {
                  const isExpanded = expandedAnswers[record.id] || false;
                  const cleanAnswer = record.answer ? record.answer.trim() : "";
                  const wordLimit = 15;
                  const words = cleanAnswer.split(/\s+/);
                  const shouldTruncate = words.length > wordLimit;

                  let displayedAnswer = cleanAnswer;
                  if (shouldTruncate && !isExpanded) {
                    displayedAnswer = words.slice(0, wordLimit).join(" ") + "...";
                  }

                  return (
                    <tr key={record.id}>
                      <td className="sno-col">{startIndex + index + 1}</td>
                      <td className="query-col">{record.query}</td>
                      <td className="answer-col">
                        <div className="answer-text-container">
                          <span>
                            {displayedAnswer}
                            {shouldTruncate && (
                              <button
                                className="show-more-toggle"
                                onClick={() => toggleExpandAnswer(record.id)}
                              >
                                {isExpanded ? "Show less" : "Show more"}
                              </button>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="uuid-col" style={{ fontSize: "11px", fontFamily: "monospace" }}>{record.answer_id}</td>
                      <td className="audio-col">
                        {record.answer_audio ? (
                          <button
                            className={`audio-play-btn ${playingAudioId === record.id ? "playing" : ""}`}
                            onClick={() => playAudio(record.id, record.answer_audio!)}
                          >
                            {playingAudioId === record.id ? (
                              <>
                                <i className="fa-solid fa-square" /> Stop
                              </>
                            ) : (
                              <>
                                <i className="fa-solid fa-play" /> Play
                              </>
                            )}
                          </button>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td className="short-col">{record.input_type || "N/A"}</td>
                      <td className="short-col">{record.language || "N/A"}</td>
                      <td className="uuid-col" style={{ fontFamily: "monospace" }}>
                        {record.sessionToken || "N/A"}
                      </td>
                      <td className="short-col">
                        <div className="status-pill">
                          <span className={`status-dot ${record.failed_to_answer ? "failed" : "success"}`} />
                          {record.failed_to_answer ? "Yes" : "No"}
                        </div>
                      </td>
                      <td className="date-col">{formatDateTime(record.createdAt)}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        {/* Empty, Loading, and Error States rendered outside scroll wrapper for perfect centering */}
        {loading ? (
          <div className="table-empty-state">
            <i className="fa-solid fa-spinner fa-spin table-empty-icon" />
            <p>Loading query logs...</p>
          </div>
        ) : error ? (
          <div className="table-empty-state">
            <i className="fa-solid fa-circle-exclamation table-empty-icon" style={{ color: "#dc2626" }} />
            <p style={{ color: "#dc2626" }}>{error}</p>
          </div>
        ) : paginatedRecords.length === 0 ? (
          <div className="table-empty-state">
            <i className="fa-solid fa-folder-open table-empty-icon" />
            <p>No queries found matching the selected filters.</p>
          </div>
        ) : null}

        {/* Pagination Controls */}
        {filteredRecords.length > 0 && (
          <div className="queries-pagination-container">
            <div className="pagination-pages">
              <button
                className="page-nav-btn"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`page-number-btn ${currentPage === page ? "active" : ""}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}
              <button
                className="page-nav-btn"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
            </div>

            <div className="pagination-stats">
              <div className="page-size-selector">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                </select>
              </div>
              <span className="total-queries-text">Total {filteredRecords.length} Queries</span>
            </div>
          </div>
        )}
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <div className="queries-toast">
          <i className="fa-solid fa-circle-check queries-toast-icon" />
          <span>{toastMessage}</span>
        </div>
      )}
    </div>
  );
};
